import { AlertCircle, X } from 'lucide-react'
import PropTypes from 'prop-types'

import { AdasModal } from '@components/wrapper-components'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { cn } from 'lib/utils'
import { getFriendlyErrorMessage } from '../../utils/errorMessage'

/** Canonical form-style modal — dark navy header strip + scrollable body + sticky footer.
 *
 *  Reference: Edit Product modal. Used everywhere a modal hosts a form
 *  (create/edit appointments, users, products, work orders, quotes, etc.).
 *
 *  Layout:
 *    ┌─────────────────────────────────────────┐
 *    │ [icon] EYEBROW                       X  │  ← dark navy header (flex-none)
 *    │        Title                            │
 *    ├─────────────────────────────────────────┤
 *    │                                         │
 *    │  ...children (scrollable body)...       │  ← flex-1 min-h-0 overflow-y-auto
 *    │                                         │
 *    ├─────────────────────────────────────────┤
 *    │              [Cancel]  [Primary]        │  ← sticky footer (flex-none)
 *    └─────────────────────────────────────────┘
 *
 *  Props:
 *    - icon: optional Lucide icon component rendered in the white/10 tile
 *    - eyebrow: optional uppercase line shown above the title
 *    - title: required header title
 *    - footer: optional JSX rendered in the sticky footer; omit to render no footer
 *    - bodyClassName: extra classes appended to the scrollable body
 *    - width: max-w utility (default 'max-w-[560px]')
 *    - maxHeight: max-h utility (default 'max-h-[90vh]')
 *    - hideClose: hide the X button (rare; e.g. fully-controlled flows)
 *
 *  Read-only users: the body and footer are wrapped in a disabled <fieldset>, so every
 *  input, select, textarea and button inside is inert without each of the 20-odd forms
 *  that use this modal having to know about the role. `display: contents` keeps the
 *  fieldset out of the layout. The header X stays outside it so the modal can still be
 *  dismissed. Note this disables native form controls only - a plain <div onClick> is
 *  unaffected, which is why the request layer refuses writes regardless.
 *
 *  `clientMayEdit` opts a form out of that: booking an appointment is the one write a
 *  client user may make, so its form has to stay fillable. The request layer and the
 *  backend only let the booking calls through, so this cannot widen what is written.
 */
export const FormModal = ({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  eyebrow,
  footer,
  children,
  width = 'max-w-[560px]',
  maxHeight = 'max-h-[90vh]',
  bodyClassName = '',
  hideClose = false,
  mobileFullScreen = false,
  errorMessage,
  onDismissError,
  clientMayEdit = false
}) => {
  const isReadOnly = useIsReadOnly() && !clientMayEdit

  return (
    <AdasModal
      open={open}
      onClose={onClose}
      className={cn(
        `flex w-[92vw] ${width} ${maxHeight} flex-col overflow-hidden p-0`,
        // On phones (≤600px) the scheduler modals fill the screen so the card/section
        // layout reads as a native full-page view rather than a cramped dialog.
        mobileFullScreen &&
          'max-[600px]:h-screen max-[600px]:max-h-none max-[600px]:w-screen max-[600px]:max-w-none max-[600px]:rounded-none'
      )}
    >
      <div className='flex min-h-0 flex-1 flex-col bg-white'>
        {/* Dark navy header */}
        <div className='flex flex-none items-center justify-between gap-3 bg-[#0A1628] px-5 py-4 text-white'>
          <div className='flex min-w-0 items-center gap-3'>
            {Icon && (
              <span className='inline-flex h-9 w-9 flex-none items-center justify-center rounded-md bg-white/10'>
                <Icon className='h-4 w-4 text-white' />
              </span>
            )}
            <div className='min-w-0'>
              {eyebrow && <div className='text-[11px] font-semibold uppercase tracking-[0.08em] text-white/70'>{eyebrow}</div>}
              <div className='truncate text-[20px] font-bold leading-tight'>{title}</div>
              {subtitle && <div className='mt-0.5 text-[13px] font-normal leading-snug text-white/70'>{subtitle}</div>}
            </div>
          </div>
          {!hideClose && (
            <button
              type='button'
              onClick={onClose}
              aria-label='Close'
              className='inline-flex h-8 w-8 flex-none items-center justify-center rounded-md bg-white/10 text-white transition-colors hover:bg-white/20'
            >
              <X className='h-4 w-4' />
            </button>
          )}
        </div>
        <fieldset disabled={isReadOnly} className='contents'>
          {/* Scrollable body */}
          <div className={`min-h-0 flex-1 overflow-y-auto px-5 py-4 ${bodyClassName}`}>
            {errorMessage && (
              <div
                role='alert'
                className='mb-4 flex items-start gap-2 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-[13px] text-[#B91C1C]'
              >
                <AlertCircle className='mt-0.5 h-4 w-4 flex-none' />
                <div className='flex-1 leading-snug'>{errorMessage}</div>
                {onDismissError && (
                  <button
                    type='button'
                    onClick={onDismissError}
                    aria-label='Dismiss error'
                    className='ml-1 inline-flex h-5 w-5 flex-none items-center justify-center rounded text-[#B91C1C] hover:bg-[#FECACA]'
                  >
                    <X className='h-3.5 w-3.5' />
                  </button>
                )}
              </div>
            )}
            {children}
          </div>
          {/* Sticky footer */}
          {footer && <div className='flex-none border-t border-[#EBEDF0] bg-white px-5 py-3'>{footer}</div>}
        </fieldset>
      </div>
    </AdasModal>
  )
}

FormModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  icon: PropTypes.elementType,
  eyebrow: PropTypes.node,
  footer: PropTypes.node,
  children: PropTypes.node,
  width: PropTypes.string,
  maxHeight: PropTypes.string,
  bodyClassName: PropTypes.string,
  hideClose: PropTypes.bool,
  mobileFullScreen: PropTypes.bool,
  errorMessage: PropTypes.node,
  onDismissError: PropTypes.func,
  clientMayEdit: PropTypes.bool
}

/** Pull a user-presentable message out of an axios / fetch error.
 *  CAR-3634: routes through the shared mapper so callers never surface raw text
 *  (stringified JSON, status enums, or JS errors). Returns '' when nothing safe is
 *  determinable, so a caller's `|| 'context fallback'` still applies. */
export const extractApiError = (err, fallback = '') => {
  if (typeof err === 'string') return err.trim()
  return getFriendlyErrorMessage(err, fallback)
}
